import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { ArrowRight, Shield, FileText, Users, Gavel, CalendarCheck, FolderOpen, LifeBuoy, MapPin, Briefcase } from "lucide-react";
import NYStateMap from "../components/NYStateMap";

const HERO_IMG = "/brand/hero-logo.jpg";

// Full services catalog shown in the homepage "Learn About Services" dialog.
const SERVICES_CATALOG = [
  {
    to: "/incarcerated",
    icon: Shield,
    title: "Incarcerated Individuals",
    docs: ["440 motions", "Article 78 petitions", "Appeals", "Disciplinary hearings", "Tier 3 appeals", "Professional grievances", "Parole packets"],
    text: "Attorney-graded pro-se documents, communication support, and clear case structure for individuals navigating the system from the inside.",
  },
  {
    to: "/families",
    icon: Users,
    title: "Families",
    docs: ["Plain-language explanations", "Document gathering", "Coordination & updates"],
    text: "Compassionate guidance, document preparation, and steady communication so families are never left in the dark.",
  },
  {
    to: "/defense",
    icon: FileText,
    title: "Defense Support",
    docs: ["440 motions", "Article 78 petitions", "Appeals", "Briefs", "Motions"],
    text: "Trial-ready document organization, discovery review, witness outlines, and attorney-ready hearing binders.",
  },
  {
    to: "/hearings",
    icon: Gavel,
    title: "Hearings",
    docs: ["Documentation packages", "Case timelines", "Mock prep", "Tier 3 appeals"],
    text: "Persuasive timelines, indexed exhibits, mock-prep checklists, and witness coordination for every type of hearing.",
  },
  {
    to: "/attorneys",
    icon: Briefcase,
    title: "Attorneys & Professional Services",
    docs: ["Document prep support", "3-day training program", "Remote cohort delivery"],
    text: "B2B services for law firms, public defenders, reentry orgs — outsource paralegal work or train your team.",
  },
];

const PILLARS = [
  { to: "/incarcerated", icon: Shield, title: "Incarcerated", text: "Document preparation, case assistance, and communication support for incarcerated individuals." },
  { to: "/families", icon: Users, title: "Families", text: "Compassionate guidance and document help for families navigating the system." },
  { to: "/defense", icon: FileText, title: "Defense", text: "Case organization, document review, and attorney-ready preparation." },
  { to: "/hearings", icon: Gavel, title: "Hearings", text: "Timelines, documentation, and structured prep. Includes disciplinary hearings and Tier 3 appeals." },
  { to: "/attorneys", icon: Briefcase, title: "Attorneys", text: "Document preparation support and a 3-day training program for legal teams, paralegals, and reentry advocates." },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    icon: CalendarCheck,
    title: "Schedule Consultation",
    text: "Book a confidential phone call or in-person consultation. We listen first.",
  },
  {
    step: "02",
    icon: FolderOpen,
    title: "Case Review & Document Collection",
    text: "We review your situation, gather what's needed, and chart a clear plan forward.",
  },
  {
    step: "03",
    icon: LifeBuoy,
    title: "Ongoing Support & Guidance",
    text: "Attorney-reviewed documents, steady communication, and structured follow-through.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="border-b border-zinc-800" data-testid="hero-section">
        <div className="container-pl py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="overline mb-6" data-testid="hero-overline">Pain births Purpose.</p>
              <h1 className="display-serif text-4xl sm:text-5xl lg:text-6xl text-zinc-50 leading-[1.05] mb-6" data-testid="hero-title">
                Structured legal support.
                <br />
                <span className="text-amber-400">When it matters most.</span>
              </h1>
              <p className="text-base lg:text-lg text-zinc-300 leading-relaxed max-w-2xl mb-8 font-light">
                Paralegal Voices of New York provides specialized litigation-support,
                procedural-support, legal research assistance, chronology organization,
                administrative-review support, and correctional procedural analysis for
                incarcerated individuals, families, advocates, attorneys, and legal professionals.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/booking">
                  <Button
                    size="lg"
                    className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white px-7 h-12"
                    data-testid="hero-schedule-btn"
                  >
                    Schedule Consultation
                    <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </Link>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="lg"
                      className="rounded-sm border-amber-400 text-amber-400 hover:bg-blue-950/30 hover:text-amber-300 px-7 h-12"
                      data-testid="hero-learn-btn"
                    >
                      Learn About Services
                    </Button>
                  </DialogTrigger>
                  <DialogContent
                    className="max-w-3xl bg-zinc-950 border border-amber-400/30 text-zinc-100 p-0 max-h-[88vh] overflow-hidden flex flex-col rounded-sm"
                    data-testid="services-dialog"
                  >
                    <DialogHeader className="px-6 sm:px-8 pt-7 pb-4 border-b border-zinc-800">
                      <p className="overline mb-3">Our five practice areas</p>
                      <DialogTitle className="display-serif text-2xl sm:text-3xl text-zinc-50 leading-tight">
                        Paralegal services <span className="text-amber-400">across the journey.</span>
                      </DialogTitle>
                      <p className="text-sm text-zinc-400 leading-relaxed mt-2 max-w-2xl">
                        From the first call to the final hearing — structured, attorney-graded support tailored to your situation.
                      </p>
                    </DialogHeader>

                    <div className="overflow-y-auto flex-1 px-6 sm:px-8 py-6 space-y-5">
                      {SERVICES_CATALOG.map((svc) => (
                        <Link
                          key={svc.to}
                          to={svc.to}
                          className="group block border border-zinc-800 hover:border-amber-400/60 bg-black/40 hover:bg-blue-950/20 transition-colors rounded-sm p-5"
                          data-testid={`services-dialog-card-${svc.to.replace("/", "")}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 h-10 w-10 rounded-sm border border-amber-400/40 bg-blue-950/30 flex items-center justify-center">
                              <svc.icon className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2">
                                <h3 className="display-serif text-lg text-zinc-50 group-hover:text-amber-300 transition-colors">
                                  {svc.title}
                                </h3>
                                <ArrowRight className="h-4 w-4 text-amber-400 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" strokeWidth={1.5} />
                              </div>
                              <p className="text-sm text-zinc-300 leading-relaxed mt-1.5">
                                {svc.text}
                              </p>
                              <div className="flex flex-wrap gap-1.5 mt-3">
                                {svc.docs.map((d) => (
                                  <span
                                    key={d}
                                    className="inline-block px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-400/90 border border-amber-400/30 rounded-sm bg-blue-950/30"
                                  >
                                    {d}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>

                    <DialogFooter className="px-6 sm:px-8 py-4 border-t border-zinc-800 bg-black/40 flex-row items-center justify-between gap-3">
                      <p className="text-[11px] text-zinc-500 italic flex-1 text-left">
                        Informational only — not legal advice.
                      </p>
                      <Link to="/booking">
                        <Button
                          size="sm"
                          className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white h-10 px-5"
                          data-testid="services-dialog-cta"
                        >
                          Schedule a Consultation
                          <ArrowRight className="ml-2 h-3.5 w-3.5" strokeWidth={1.5} />
                        </Button>
                      </Link>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-sm overflow-hidden border border-amber-400/30 aspect-square bg-black">
                <img
                  src={HERO_IMG}
                  alt="Paralegal Voices of New York"
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  width="1100"
                  height="1100"
                  data-testid="hero-image"
                />
              </div>
              {/* Seal-red corner badge — drawn from logo accent */}
              <div className="hidden lg:flex absolute -bottom-4 -left-4 bg-seal-red text-white px-4 py-3 border border-amber-400/40 shadow-2xl items-center gap-3" data-testid="hero-seal">
                <div className="h-8 w-px bg-amber-400/70" />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.22em] text-amber-300">EST · NY</p>
                  <p className="display-serif text-sm leading-none mt-1">Voices that matter.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 3-step */}
      <section className="border-b border-zinc-800 bg-zinc-950" data-testid="how-it-works-section">
        <div className="container-pl py-16 lg:py-20">
          <div className="max-w-2xl mb-12">
            <p className="overline mb-4">How it works</p>
            <h2 className="display-serif text-3xl sm:text-4xl text-zinc-50 leading-tight accent-rule">
              A clear, simple path forward.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.step} className="bg-zinc-950 p-8" data-testid={`hiw-step-${s.step}`}>
                <p className="font-mono text-xs text-amber-400 mb-6 tracking-[0.2em]">{s.step}</p>
                <s.icon className="h-7 w-7 text-amber-400 mb-5" strokeWidth={1.25} />
                <h3 className="display-serif text-xl text-zinc-50 mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-300 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRACTICE AREAS */}
      <section className="border-b border-zinc-800 bg-black" data-testid="pillars-section">
        <div className="container-pl py-16 lg:py-20">
          <div className="max-w-2xl mb-12">
            <p className="overline mb-4">Practice areas</p>
            <h2 className="display-serif text-3xl sm:text-4xl text-zinc-50 leading-tight accent-rule">
              Five areas. One standard of care.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
            {PILLARS.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                className="group bg-zinc-950 p-8 transition-colors hover:bg-blue-950/40"
                data-testid={`pillar-${p.title.toLowerCase()}`}
              >
                <p.icon className="h-6 w-6 text-amber-400 mb-5" strokeWidth={1.25} />
                <h3 className="display-serif text-xl text-zinc-50 mb-2">{p.title}</h3>
                <p className="text-sm text-zinc-300 leading-relaxed mb-6">{p.text}</p>
                <span className="text-sm text-amber-400 inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                  Learn more <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SERVING NEW YORK — Outlined Map */}
      <section className="border-b border-zinc-800 bg-zinc-950" data-testid="ny-map-section">
        <div className="container-pl py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <p className="overline mb-4">Serving New York</p>
              <h2 className="display-serif text-3xl sm:text-4xl text-zinc-50 leading-tight mb-6 accent-rule">
                Statewide presence. Personal attention.
              </h2>
              <p className="text-base text-zinc-300 leading-relaxed mb-8 font-light">
                From the five boroughs to the Adirondacks, we support clients across New York State —
                court filings, hearing preparation, and document services wherever your case is.
              </p>

              <ul className="space-y-3 mb-8" data-testid="ny-regions">
                {[
                  { name: "New York City", note: "All five boroughs" },
                  { name: "Long Island", note: "Nassau & Suffolk" },
                  { name: "Hudson Valley", note: "Westchester to Albany" },
                  { name: "Western & Upstate NY", note: "Buffalo, Rochester, Syracuse" },
                ].map((r) => (
                  <li key={r.name} className="flex items-start gap-3 pb-3 border-b border-zinc-800 last:border-0">
                    <MapPin className="h-4 w-4 text-amber-400 mt-1 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-zinc-50">{r.name}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">{r.note}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <Link to="/booking">
                <Button variant="outline" className="rounded-sm border-amber-400 text-amber-400 hover:bg-blue-950/30 h-11" data-testid="ny-map-cta">
                  Schedule from anywhere in NY
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="relative bg-black border border-zinc-800 rounded-sm aspect-[4/3] p-8 lg:p-10">
                <div
                  className="absolute inset-0 opacity-[0.08]"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, #FBBF24 1px, transparent 1px), linear-gradient(to bottom, #FBBF24 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                  }}
                />
                <NYStateMap className="relative w-full h-full text-amber-400" strokeWidth={1.5} />
                <div className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.22em] text-zinc-500">NY · USA</div>
                <div className="absolute top-5 right-5 font-mono text-[10px] tracking-[0.22em] text-amber-400">◉ NYC</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-700 text-white" data-testid="cta-section">
        <div className="container-pl py-14 lg:py-16">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h2 className="display-serif text-3xl sm:text-4xl leading-tight mb-3">
                Have questions? We're listening.
              </h2>
              <p className="text-blue-100 leading-relaxed max-w-xl font-light">
                Schedule a phone call or in-person consultation. We'll discuss your situation and
                tell you exactly how we can help.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/booking">
                <Button
                  size="lg"
                  className="rounded-sm bg-zinc-950 text-amber-400 hover:bg-blue-950/30 h-12 px-7"
                  data-testid="cta-schedule-btn"
                >
                  Schedule Consultation
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
